import {
  PieChart as RechartsPie,
  ResponsiveContainer,
  Pie,
  Cell,
  Legend,
  Tooltip
} from "recharts";
import { chartsPalette } from "src/design/ColorPalette";
import { CustomText } from "src/design/CustomText";
import { Grid, Tooltip as MuiTooltip, IconButton } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { Box } from "@mui/system";
import NoDataFound from "src/assets/no-data-found.png";

interface Props {
  data: {
    name: string;
    value: number;
  }[];
  title?: string;
  tooltip?: string;
  palette?: string[];
  pieHeight?: number;
  labelSize?: number;
}

interface CustomLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  index: number;
  name: string;
}

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = (props: CustomLabelProps) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, name } = props;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`} ({name})
    </text>
  );
};

export const PieChart = ({ data, title, tooltip, palette, pieHeight, labelSize }: Props) => {
  const colorPalette = palette || chartsPalette[`${data.length}`];

  return (
    <Grid container width={"100%"} height={"100%"}>
      <Grid item textAlign={"left"} marginLeft={5} width={"100%"}>
        {title && (
          <Grid container alignItems={"center"} width={"100%"}>
            <Grid item>
              <CustomText component={"h3"} type="subheader" label={title} />
            </Grid>
            {tooltip && (
              <MuiTooltip title={tooltip} sx={{ padding: 0, marginLeft: 0.5 }}>
                <IconButton>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
            )}
          </Grid>
        )}
      </Grid>
      {data.length === 0 ? (
        <Grid container justifyContent={"center"}>
          <img src={NoDataFound} alt="No data found" />
        </Grid>
      ) : (
        <ResponsiveContainer width={"100%"} height={pieHeight ?? 350}>
          <RechartsPie>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              fill="#8884d8"
              dataKey="value"
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={colorPalette[index % colorPalette.length]}
                />
              ))}
            </Pie>
            <Tooltip />
          </RechartsPie>
        </ResponsiveContainer>
      )}
    </Grid>
  );
};
